import { useCallback, useEffect, useMemo, useState } from 'react';

import { faChild } from '@fortawesome/pro-duotone-svg-icons';
import {
  faFolder,
  faFolderPlus,
  faPlus
} from '@fortawesome/pro-light-svg-icons';
import classNames from 'classnames';
import { isEqual } from 'date-fns';
import { useDispatch, usePlanUnitsOptions, useSelector } from 'hooks';
import { useTranslation } from 'react-i18next';
import { setUserPreference } from 'redux/actionCreators';
import styled from 'styled-components';

import { Interval, PlanGroup, PlanUnit, PlanUnitMix } from 'components/types';

import { PlanUnitType } from '../../enums';

import { RED } from 'constants/colors';

import { getPlanunitCommaIds } from 'components/utils';
import { getMinDate } from 'utils/date';
import { usePrevious } from 'utils/reactHooks';

import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';

import {
  ArrowNavigation,
  FontAwesomeSvgIcon,
  VirtualizedAutocomplete
} from 'components/shared';
import {
  checkboxWidth,
  iconWidth,
  optionPadding
} from 'components/shared/Autocomplete/MuiAutocompleteOption';
import { defaultListWidth } from 'components/shared/Autocomplete/VirtualizedAutocomplete';
import PlanGroupsDialog from 'components/shared/PlanGroups/PlanGroupsDialog';

import {
  useFilterOptions,
  useGetOptionLabel,
  useRenderOption
} from './planUnitSelectorHooks';
import { getDefaultInterval, isValidPlanUnit } from './util';

const PlanUnitSelectorStyles = styled.div`
  display: flex;
  align-items: flex-end;

  .MuiAutocomplete-root {
    min-width: ${defaultListWidth}px;

    .input-box {
      display: flex;
      justify-content: space-between;
      padding-right: 0 !important;

      input {
        margin-right: 5px;
      }
    }

    .add-more {
      visibility: hidden;
      padding: 4px;
    }

    &:hover {
      .add-more {
        visibility: visible;
      }
    }

    .MuiChip-root {
      margin: 0 3px 0 0;
      background-color: unset;
      height: auto;
      border: 0;

      .MuiChip-label {
        padding-left: 0;
      }
    }

    .MuiAutocomplete-tag {
      height: auto;
    }

    .invalid {
      color: ${RED};

      input {
        color: ${RED};
      }
    }
  }

  &.full-width {
    .MuiAutocomplete-root {
      min-width: auto;
    }
  }
`;

export type PlanUnitSelectorProps<T, U> = {
  className?: string;
  defaultValue?: PlanUnit[] | PlanUnitMix[] | undefined;
  onChange?: (options: PlanUnit[] | PlanUnitMix[]) => void;
  firstOptionIsDefault?: boolean;
  interval?: Interval;
  tempOptions?: PlanUnit[] | PlanUnitMix[] | undefined;
  useEmployeePlanunits?: boolean;
  usePublicPlanUnits?: boolean;
  fullWidth?: boolean;
  hideGroups?: true;
  isSingleSelect?: true;
  hideNavigation?: T;
  showNavigationCnt?: T extends true ? never : boolean;
  doNotPersist?: U;
  shouldSaveCCPreferenceKey?: U extends true
    ? never
    : 'shouldSaveCostCenterSelection' | 'flexShouldSaveCostCenterSelection';
  lastUsedCCPreferenceKey?: U extends true
    ? never
    : 'lastUsedCostCenterSelection' | 'flexLastUsedCostCenterSelection';
  disabled?: true;
  filterByType?: PlanUnitType;
  doNotPreselectOnlyOption?: boolean;
  error?: boolean;
  helperText?: string;
  variant?: 'standard' | 'outlined' | 'filled';
  size?: 'small' | 'medium';
  onClear?: () => void;
} & (
  | {
      isSingleSelect: true;
      onSelected: (options: PlanUnit[]) => void;
    }
  | {
      hideGroups: true;
      onSelected: (options: PlanUnit[]) => void;
    }
  | {
      isSingleSelect?: never;
      hideGroups?: never;
      onSelected: (options: PlanUnitMix[]) => void;
    }
);

export default function PlanUnitSelector<T extends boolean, U extends boolean>(
  props: PlanUnitSelectorProps<T, U>
) {
  const {
    className,
    defaultValue,
    onChange: fieldOnChange,
    doNotPreselectOnlyOption,
    firstOptionIsDefault,
    interval: intervalProp,
    tempOptions,
    useEmployeePlanunits = false,
    usePublicPlanUnits,
    fullWidth,
    hideNavigation,
    hideGroups,
    doNotPersist,
    isSingleSelect,
    disabled,
    filterByType,
    error,
    helperText,
    onClear,
    variant = 'standard',
    size = 'small'
  } = props;

  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const { preferences } = useSelector((state) => state);

  const shouldSaveCostCenterSelection =
    preferences[
      (!props.doNotPersist && props.shouldSaveCCPreferenceKey) ||
        'shouldSaveCostCenterSelection'
    ] ?? true;
  const lastUsedCostCenterSelection =
    preferences[
      (!props.doNotPersist && props.lastUsedCCPreferenceKey) ||
        'lastUsedCostCenterSelection'
    ] || [];
  const { hasKidsPlan } = useSelector((state) => state.grants);
  const interval = useMemo(
    () => intervalProp || getDefaultInterval(),
    [intervalProp]
  );
  const compareInterval = useMemo(
    () =>
      isEqual(interval.startDate, getMinDate())
        ? getDefaultInterval()
        : interval,
    [interval]
  );

  const options = usePlanUnitsOptions({
    showGroups: !hideGroups,
    useEmployeePlanunits,
    usePublicPlanUnits,
    interval,
    filterByType
  });
  const [hasUserSelection, setHasUserSelection] = useState(false);
  const [inputValue, setInputValue] = useState('');

  const [selectedOptionsIds, setSelectedOptionsIds] = useState<
    (string | number)[]
  >(
    tempOptions
      ? tempOptions.map((o) => o.id)
      : !doNotPersist && shouldSaveCostCenterSelection
      ? lastUsedCostCenterSelection
      : []
  );

  const getSelectedOptions = (): (PlanUnit | PlanGroup)[] => {
    if (
      options.length === 1 &&
      !('costCenters' in options[0]) &&
      !doNotPreselectOnlyOption
    ) {
      return [options[0]];
    }

    if (selectedOptionsIds.length) {
      return options.filter((o) => selectedOptionsIds.includes(o.uniqId));
    }

    if (hasUserSelection) {
      return []; // User has cleared the selection
    }

    let uniqIds: (string | number)[] = [];

    if (defaultValue && !(!doNotPersist && shouldSaveCostCenterSelection)) {
      uniqIds = defaultValue.map((o) => (o.uniqId ? o.uniqId : o.id));
    } else if (firstOptionIsDefault && !hasUserSelection) {
      uniqIds = options
        .filter((o) => !('costCenters' in o))
        .slice(0, 1)
        .map((o) => o.uniqId);
    }

    const selectedOptions = uniqIds
      .map((id) => options.filter(({ uniqId }) => uniqId === id)[0])
      .filter((v) => v);

    return selectedOptions;
  };

  const selectedOptions = getSelectedOptions();

  const getOptionLabel = useGetOptionLabel();
  const filterOptions = useFilterOptions();
  const renderOption = useRenderOption({ compareInterval });
  const renderMultiOption = useRenderOption({ isMulti: true, compareInterval });
  const [showPlanGroupsDialog, setShowPlanGroupsDialog] = useState(false);
  const [isOpen, setOpen] = useState(false);
  const isMulti = selectedOptions.length > 1 || isOpen;
  const extraWidth =
    (isMulti ? checkboxWidth : 0) +
    (hasKidsPlan ? iconWidth : 0) +
    optionPadding * 2;

  const saveSelection = (value: (string | number)[]) => {
    dispatch(
      setUserPreference({
        key: props.lastUsedCCPreferenceKey || 'lastUsedCostCenterSelection',
        value
      })
    );
  };

  const onChange = (e, value) => {
    const selectionList: PlanUnitMix[] = Array.isArray(value)
      ? value.filter((v) => v)
      : value
      ? [value]
      : [];
    const ids = selectionList.map((o) => o.uniqId);
    setSelectedOptionsIds(ids);
    if (!doNotPersist && shouldSaveCostCenterSelection) {
      saveSelection(ids);
    }
    setHasUserSelection(true);
    fieldOnChange && fieldOnChange(selectionList);
  };

  const renderInput = useCallback(
    (params) => {
      const selectedOption = selectedOptions[0];
      const isGroup = selectedOption && 'costCenters' in selectedOption;

      if (
        selectedOption &&
        ('costCenters' in selectedOption ||
          (hasKidsPlan && selectedOption.isKids))
      ) {
        params.InputProps.startAdornment = (
          <InputAdornment position="start">
            <FontAwesomeSvgIcon
              icon={isGroup ? faFolder : faChild}
              fontSize="small"
            />
          </InputAdornment>
        );
      }

      if (!isSingleSelect && !disabled) {
        params.InputProps.className += ' input-box';
        params.InputProps.endAdornment = (
          <div>
            <IconButton
              size="small"
              className="add-more"
              title={t('addMore')}
              onClick={() => {
                setOpen(true);
              }}
            >
              <FontAwesomeSvgIcon icon={faPlus} fontSize="small" />
            </IconButton>
            {params.InputProps.endAdornment.props.children}
          </div>
        );
      }

      if (
        selectedOption &&
        !isGroup &&
        !isValidPlanUnit(selectedOption.validity, compareInterval)
      ) {
        params.inputProps.value = `${selectedOption.name} (${t(
          'invalid'
        ).toLowerCase()})`;
        params.InputProps.className += ' invalid';
      }

      return (
        <TextField
          label={t('planUnits')}
          error={error}
          variant={variant}
          helperText={helperText}
          size="small"
          {...params}
        />
      );
    },
    [hasKidsPlan, selectedOptions, compareInterval, t]
  );

  const renderTags = (options: PlanUnitMix[]) =>
    options.map((option) => {
      const isInvalid =
        !('costCenters' in option) &&
        !isValidPlanUnit(option.validity, compareInterval);
      return (
        <Chip
          key={option.uniqId}
          label={
            isInvalid
              ? `${option.name} (${t('invalid').toLowerCase()})`
              : option.name
          }
          variant="outlined"
          className={classNames({ invalid: isInvalid })}
          onDelete={() =>
            onChange(
              null,
              selectedOptions.filter((o) => o.uniqId !== option.uniqId)
            )
          }
        />
      );
    });
  const onArrowNavigate = (direction, currentIndex) => {
    let nextOption: PlanUnitMix;
    if (direction === 'prev') {
      nextOption = options[currentIndex - 1];
    } else {
      nextOption = selectedOptions[0] ? options[currentIndex + 1] : options[0];
      if ('costCenters' in nextOption) {
        const startIndex = selectedOptions[0] ? currentIndex + 1 : 0; // skip plangroups
        const planUnit = options.find(
          (o, i) => i > startIndex && !('costCenters' in o)
        );
        if (planUnit) {
          nextOption = planUnit;
        }
      }
    }
    onChange(null, [nextOption]);
  };

  const prevSelectedOptions = usePrevious(selectedOptions);
  useEffect(() => {
    const existingIds =
      prevSelectedOptions && getPlanunitCommaIds(prevSelectedOptions);
    const newIds = getPlanunitCommaIds(selectedOptions);
    if ((existingIds || newIds) && newIds !== existingIds) {
      props.onSelected(selectedOptions as PlanUnit[]);
    }
  }, [selectedOptions, prevSelectedOptions]);

  const prevShouldSaveCostCenterSelection = usePrevious(
    shouldSaveCostCenterSelection
  );
  useEffect(() => {
    if (
      !doNotPersist &&
      prevShouldSaveCostCenterSelection !== undefined &&
      prevShouldSaveCostCenterSelection !== shouldSaveCostCenterSelection
    ) {
      if (shouldSaveCostCenterSelection) {
        saveSelection(selectedOptions.map((o) => o.uniqId));
      } else {
        saveSelection([]);
      }
    }
  }, [prevShouldSaveCostCenterSelection, shouldSaveCostCenterSelection]);

  return (
    <PlanUnitSelectorStyles
      className={classNames(className, 'planunit-selector', {
        'full-width': fullWidth
      })}
    >
      {isMulti ? (
        <>
          <VirtualizedAutocomplete
            autoHighlight
            disableCloseOnSelect
            extraWidth={extraWidth}
            filterOptions={filterOptions}
            getLimitTagsText={(cnt) =>
              `${cnt} ${t('selected').toLocaleLowerCase()}`
            }
            getOptionLabel={getOptionLabel}
            isOptionEqualToValue={(a, b) => a.uniqId === b.uniqId}
            limitTags={0}
            multiple
            onChange={onChange}
            onClose={() => setOpen(false)}
            onOpenCallback={() => setOpen(true)}
            open={isOpen}
            options={options}
            size="small"
            renderInput={(params) => (
              <TextField
                label={t('planUnits')}
                error={error}
                helperText={helperText}
                variant={variant}
                {...params}
                size="small"
              />
            )}
            renderOption={renderMultiOption}
            renderTags={renderTags}
            value={selectedOptions}
            fullWidth={fullWidth}
            inputValue={inputValue}
            onInputChange={(event, value, reason) => {
              reason === 'clear' && onClear && onClear();

              if (event && event.type === 'blur') {
                setInputValue('');
              } else if (reason !== 'reset') {
                setInputValue(value);
              }
            }}
            disabled={disabled}
          />
        </>
      ) : (
        <VirtualizedAutocomplete
          autoHighlight
          extraWidth={extraWidth}
          filterOptions={filterOptions}
          getOptionLabel={getOptionLabel}
          isOptionEqualToValue={(a, b) => a.uniqId === b.uniqId}
          onChange={onChange}
          options={options}
          renderInput={renderInput}
          renderOption={renderOption}
          value={selectedOptions[0] || null}
          onInputChange={(event, value, reason) => {
            reason === 'clear' && onClear && onClear();
          }}
          fullWidth={fullWidth}
          disabled={disabled}
          size={size}
        />
      )}
      {(!hideNavigation || !hideGroups) && !disabled && (
        <div className="d-flex v-center">
          <ArrowNavigation
            onNavigate={onArrowNavigate}
            options={options}
            selectedOption={selectedOptions[0]}
            disabled={isMulti}
            showCnt={!props.hideNavigation && props.showNavigationCnt}
          />
          {!hideGroups && (
            <Tooltip title={<span>{t('editPlanGroups')}</span>}>
              <IconButton
                color="primary"
                onClick={() => setShowPlanGroupsDialog(true)}
                className="edit-plangroups"
                size="small"
                disabled={disabled}
              >
                <FontAwesomeSvgIcon icon={faFolderPlus} fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </div>
      )}
      {showPlanGroupsDialog && (
        <PlanGroupsDialog onHidden={() => setShowPlanGroupsDialog(false)} />
      )}
    </PlanUnitSelectorStyles>
  );
}
